import React, {
    createContext,
    Dispatch,
    ReactNode,
    SetStateAction,
    useMemo,
    useState,
    useContext,
    useCallback
} from 'react';
import { InsightsStatistics } from './api/types/Insights';
import { AZURE_ENVIRONMENT, DEFAULT_LAST_CHANGES_FILTER } from './constants';
import useGetInsightStatistics from './api/queries/useGetInsightStatistics';
import useHasPermissions from '../../hooks/useHasPermissions';
import { Pages, Roles } from '../../context/contexts/UserContext/permissionsTypes';

interface IspmContextProperties {
    categories: string[] | undefined;
    setCategories: Dispatch<SetStateAction<string[] | undefined>>;
    customInsightsIdsToNames: Record<string, string>;
    hasUserConfigureCustomInsight: boolean;
    domainFilter: string[] | null;
    setDomainFilter: Dispatch<SetStateAction<string[] | null>>;
    insightsData: InsightsStatistics | null;
    isLoadingPageData: boolean;
    searchedExposureName?: string;
    setSearchedExposureName: Dispatch<SetStateAction<string | undefined>>;
    selectedInsight: string | null;
    setSelectedInsight: Dispatch<SetStateAction<string | null>>;
    selectedLastChangesFilter: string;
    setSelectedLastChangesFilter: Dispatch<SetStateAction<string>>;
    severitiesFilter: number[] | undefined;
    setSeveritiesFilter: Dispatch<SetStateAction<number[] | undefined>>;
    showEmptyAndHiddenExposures: boolean;
    setShowEmptyAndHiddenExposures: Dispatch<SetStateAction<boolean>>;
    refreshInsightsData: () => void;
    hasIspmEditorPermission: boolean;
}

const DEFAULT_ISPM_CONTEXT: IspmContextProperties = {
    categories: undefined,
    setCategories: () => {},
    customInsightsIdsToNames: {},
    hasUserConfigureCustomInsight: false,
    domainFilter: null,
    setDomainFilter: () => {},
    insightsData: null,
    isLoadingPageData: false,
    setSearchedExposureName: () => {},
    selectedInsight: null,
    setSelectedInsight: () => {},
    selectedLastChangesFilter: DEFAULT_LAST_CHANGES_FILTER,
    setSelectedLastChangesFilter: () => {},
    severitiesFilter: undefined,
    setSeveritiesFilter: () => {},
    showEmptyAndHiddenExposures: false,
    setShowEmptyAndHiddenExposures: () => {},
    refreshInsightsData: () => {},
    hasIspmEditorPermission: false
};

const IspmContext = createContext<IspmContextProperties>(DEFAULT_ISPM_CONTEXT);

export const IspmProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [categories, setCategories] = useState<string[]>();
    const [domainFilter, setDomainFilter] = useState<string[] | null>(null);
    const [searchedExposureName, setSearchedExposureName] = useState<string>();
    const [selectedInsight, setSelectedInsight] = useState<string | null>(null);
    const [selectedLastChangesFilter, setSelectedLastChangesFilter] = useState(DEFAULT_LAST_CHANGES_FILTER);
    const [severitiesFilter, setSeveritiesFilter] = useState<number[]>();
    const [showEmptyAndHiddenExposures, setShowEmptyAndHiddenExposures] = useState(false);
    const {
        data: insightsData,
        isFetching,
        fetchData: refreshInsightsStatistics
    } = useGetInsightStatistics({
        diffInDays: selectedLastChangesFilter,
        domainFilter,
        environmentFilter: AZURE_ENVIRONMENT
    });

    const hasIspmEditorPermission = useHasPermissions({ permission: Pages.INSIGHTS, role: Roles.EDITOR });

    const customInsightsIdsToNames: Record<string, string> = useMemo(() => {
        const map = {};

        if (!insightsData?.customInsights?.UserCustomInsights) return map;

        insightsData.customInsights.UserCustomInsights.forEach(({ id, name }) => {
            map[id] = name;
        });

        return map;
    }, [insightsData?.customInsights?.UserCustomInsights]);

    const hasUserConfigureCustomInsight = Object.keys(customInsightsIdsToNames).length > 0;

    const refreshInsightsData = useCallback(() => {
        refreshInsightsStatistics();
    }, [refreshInsightsStatistics]);

    return (
        <IspmContext.Provider
            value={{
                categories,
                setCategories,
                customInsightsIdsToNames,
                hasUserConfigureCustomInsight,
                domainFilter,
                setDomainFilter,
                insightsData: insightsData ?? null,
                isLoadingPageData: isFetching,
                searchedExposureName,
                setSearchedExposureName,
                selectedInsight,
                setSelectedInsight,
                selectedLastChangesFilter,
                setSelectedLastChangesFilter,
                severitiesFilter,
                setSeveritiesFilter,
                showEmptyAndHiddenExposures,
                setShowEmptyAndHiddenExposures,
                refreshInsightsData,
                hasIspmEditorPermission
            }}>
            {children}
        </IspmContext.Provider>
    );
};

export const useIspmContext = () => useContext<IspmContextProperties>(IspmContext);
